import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Area, AreaChart, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from "recharts";
import {
  TrendingUp, Upload, Save, Loader2, FileSpreadsheet,
  BarChart3, GitCompare, Calendar, Plus, Trash2, Info, Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const PARAM_LABELS = {
  annual_order_value: "Commande annuelle (FCFA)",
  unsold_value: "Valeur des invendus (FCFA)",
  expired_stock_value: "Valeur stock périmé (FCFA)",
  destruction_volume: "Volume destructions (nb lots)",
  destruction_value: "Coût des destructions (FCFA)",
  total_stock_value: "Valeur totale du stock (FCFA)",
  total_products_count: "Nombre de produits",
};

const COLORS = { my: "#0EA5E9", peer: "#F59E0B", global: "#8B5CF6" };

const AnalyticsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("history");
  const [historicalData, setHistoricalData] = useState([]);
  const [benchmark, setBenchmark] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareBy, setCompareBy] = useState("commune");
  const [parameter, setParameter] = useState("annual_order_value");
  const [newEntry, setNewEntry] = useState({
    year: new Date().getFullYear() - 1,
    annual_order_value: "", unsold_value: "", expired_stock_value: "",
    destruction_volume: "", destruction_value: "", total_stock_value: "",
    total_products_count: "", notes: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const loadHistorical = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/historical`, { headers });
      setHistoricalData(res.data);
    } catch (err) {
      toast.error("Erreur de chargement des données historiques");
    }
  }, [token]);

  const loadBenchmark = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/benchmark`, {
        headers, params: { compare_by: compareBy, parameter }
      });
      setBenchmark(res.data);
    } catch (err) {
      toast.error("Erreur de chargement du benchmark");
    } finally {
      setLoading(false);
    }
  }, [token, compareBy, parameter]);

  useEffect(() => { loadHistorical(); }, [loadHistorical]);
  useEffect(() => { if (activeTab === "benchmark") loadBenchmark(); }, [activeTab, loadBenchmark]);

  const handleSaveEntry = async () => {
    setLoading(true);
    try {
      const payload = {
        ...newEntry,
        annual_order_value: parseFloat(newEntry.annual_order_value) || 0,
        unsold_value: parseFloat(newEntry.unsold_value) || 0,
        expired_stock_value: parseFloat(newEntry.expired_stock_value) || 0,
        destruction_volume: parseInt(newEntry.destruction_volume) || 0,
        destruction_value: parseFloat(newEntry.destruction_value) || 0,
        total_stock_value: parseFloat(newEntry.total_stock_value) || 0,
        total_products_count: parseInt(newEntry.total_products_count) || 0,
      };
      await axios.post(`${API}/analytics/historical`, payload, { headers });
      toast.success(`Données ${newEntry.year} enregistrées`);
      loadHistorical();
      setNewEntry(prev => ({ ...prev, year: prev.year - 1 }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/analytics/historical/import-file`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success(`${res.data.imported_count} années importées`);
      if (res.data.errors_count > 0) {
        toast.warning(`${res.data.errors_count} erreurs lors de l'import`);
      }
      loadHistorical();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const handleExcelExport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/export/analytics/excel`, {
        headers, responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `analyse_${new Date().toISOString().split("T")[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch { toast.error("Erreur d'export Excel"); }
    finally { setLoading(false); }
  };

  const tabs = [
    { id: "history", label: "Données historiques", icon: Calendar },
    { id: "evolution", label: "Mon évolution", icon: TrendingUp },
    { id: "benchmark", label: "Benchmark", icon: GitCompare },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="analytics-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-cyan-500" />
              Analyse & Performance
            </h1>
            <p className="text-slate-500 text-sm mt-1">Suivez votre évolution et comparez-vous avec le marché</p>
          </div>
          <Button onClick={handleExcelExport} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-cyan-500/10 pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`analytics-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all
                ${activeTab === tab.id ? "bg-cyan-500/10 text-cyan-400 border-b-2 border-cyan-500" : "text-slate-500 hover:text-slate-200 hover:bg-white/5"}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Manual Entry */}
            <Card className="medical-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-cyan-500" />
                  Saisie manuelle
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Année</Label>
                    <Input type="number" min="2000" max="2030" value={newEntry.year}
                      onChange={e => setNewEntry({ ...newEntry, year: parseInt(e.target.value) || 0 })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-year" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Commande annuelle (FCFA)</Label>
                    <Input type="number" value={newEntry.annual_order_value} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, annual_order_value: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-order" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Valeur invendus (FCFA)</Label>
                    <Input type="number" value={newEntry.unsold_value} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, unsold_value: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-unsold" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Stock périmé — valeur des produits périmés en stock (FCFA)</Label>
                    <Input type="number" value={newEntry.expired_stock_value} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, expired_stock_value: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-expired" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Volume destructions (nb lots)</Label>
                    <Input type="number" value={newEntry.destruction_volume} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, destruction_volume: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-destvol" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Coût des destructions — montant facturé pour la destruction (FCFA)</Label>
                    <Input type="number" value={newEntry.destruction_value} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, destruction_value: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-destval" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Valeur totale stock (FCFA)</Label>
                    <Input type="number" value={newEntry.total_stock_value} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, total_stock_value: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-stockval" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Nombre de produits</Label>
                    <Input type="number" value={newEntry.total_products_count} placeholder="0"
                      onChange={e => setNewEntry({ ...newEntry, total_products_count: e.target.value })}
                      className="bg-slate-900 border-white/10 text-white" data-testid="history-prodcount" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Notes (optionnel)</Label>
                  <Input value={newEntry.notes} placeholder="Commentaire sur cette année..."
                    onChange={e => setNewEntry({ ...newEntry, notes: e.target.value })}
                    className="bg-slate-900 border-white/10 text-white" data-testid="history-notes" />
                </div>
                <Button onClick={handleSaveEntry} disabled={loading} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-history-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* CSV Import + Data Table */}
            <div className="space-y-4">
              <Card className="medical-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Upload className="w-5 h-5 text-amber-500" />
                    Import de fichier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-amber-500/10 rounded-lg p-3 mb-3">
                    <p className="text-xs text-amber-400 flex items-center gap-1"><Info className="w-3 h-3" /> Formats acceptés : CSV, Excel (.xlsx), PDF</p>
                    <code className="text-[10px] text-amber-400 block mt-1">
                      Colonnes : année, commande_annuelle, valeur_invendus, valeur_perimes, volume_destructions, valeur_destructions, valeur_stock, nombre_produits
                    </code>
                  </div>
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-cyan-500/20 rounded-lg cursor-pointer hover:bg-cyan-500/100/10 transition-colors">
                    <FileSpreadsheet className="w-5 h-5 text-cyan-500" />
                    <span className="text-sm text-cyan-400 font-medium">Choisir un fichier (CSV, Excel, PDF)</span>
                    <input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={handleFileImport} className="hidden" data-testid="file-import-input" />
                  </label>
                </CardContent>
              </Card>

              <Card className="medical-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white">Données enregistrées</CardTitle>
                </CardHeader>
                <CardContent>
                  {historicalData.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">Aucune donnée historique. Commencez par saisir ou importer vos données.</p>
                  ) : (
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full text-xs" data-testid="historical-data-table">
                        <thead>
                          <tr className="text-slate-500 border-b sticky top-0 bg-slate-900">
                            <th className="text-left py-2 px-1">Année</th>
                            <th className="text-right py-2 px-1">Commandes</th>
                            <th className="text-right py-2 px-1">Invendus</th>
                            <th className="text-right py-2 px-1">Périmés</th>
                            <th className="text-right py-2 px-1">Stock</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historicalData.map((d, i) => (
                            <tr key={i} className="border-b border-slate-50">
                              <td className="py-2 px-1 font-semibold text-slate-200">{d.year}</td>
                              <td className="py-2 px-1 text-right text-slate-600">{(d.annual_order_value || 0).toLocaleString()}</td>
                              <td className="py-2 px-1 text-right text-amber-400">{(d.unsold_value || 0).toLocaleString()}</td>
                              <td className="py-2 px-1 text-right text-red-400">{(d.expired_stock_value || 0).toLocaleString()}</td>
                              <td className="py-2 px-1 text-right text-cyan-400">{(d.total_stock_value || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Evolution Tab */}
        {activeTab === "evolution" && (
          <div className="space-y-6">
            {historicalData.length < 2 ? (
              <Card className="medical-card">
                <CardContent className="p-8 text-center">
                  <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Saisissez au moins 2 années de données pour voir votre évolution.</p>
                  <Button onClick={() => setActiveTab("history")} className="mt-4 bg-cyan-600 hover:bg-cyan-700 text-white">
                    Saisir mes données
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Financial Evolution */}
                <Card className="medical-card" data-testid="evolution-financial-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-white">Évolution financière</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <AreaChart data={historicalData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="year" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={11} tickFormatter={v => `${(v / 1000000).toFixed(1)}M`} />
                        <Tooltip formatter={(v) => `${v.toLocaleString()} FCFA`} contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                        <Legend />
                        <Area type="monotone" dataKey="annual_order_value" name="Commandes" stroke="#0EA5E9" fill="#0EA5E9" fillOpacity={0.1} strokeWidth={2} />
                        <Area type="monotone" dataKey="total_stock_value" name="Stock" stroke="#10B981" fill="#10B981" fillOpacity={0.1} strokeWidth={2} />
                        <Area type="monotone" dataKey="unsold_value" name="Invendus" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.1} strokeWidth={2} />
                        <Area type="monotone" dataKey="expired_stock_value" name="Périmés" stroke="#EF4444" fill="#EF4444" fillOpacity={0.1} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Destruction Evolution */}
                <Card className="medical-card" data-testid="evolution-destruction-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-white">Évolution des destructions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={historicalData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="year" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                        <Legend />
                        <Bar dataKey="destruction_volume" name="Volume" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="destruction_value" name="Valeur (FCFA)" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Ratios */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {historicalData.slice(-1).map((d, i) => {
                    const unsoldRatio = d.annual_order_value > 0 ? ((d.unsold_value / d.annual_order_value) * 100).toFixed(1) : 0;
                    const expiredRatio = d.total_stock_value > 0 ? ((d.expired_stock_value / d.total_stock_value) * 100).toFixed(1) : 0;
                    const destRatio = d.annual_order_value > 0 ? ((d.destruction_value / d.annual_order_value) * 100).toFixed(1) : 0;
                    return [
                      { label: "Ratio invendus/commandes", value: `${unsoldRatio}%`, color: parseFloat(unsoldRatio) > 15 ? "red" : parseFloat(unsoldRatio) > 8 ? "amber" : "green" },
                      { label: "Ratio périmés/stock", value: `${expiredRatio}%`, color: parseFloat(expiredRatio) > 10 ? "red" : parseFloat(expiredRatio) > 5 ? "amber" : "green" },
                      { label: "Ratio destructions/commandes", value: `${destRatio}%`, color: parseFloat(destRatio) > 12 ? "red" : parseFloat(destRatio) > 6 ? "amber" : "green" },
                    ].map((r, j) => (
                      <Card key={j} className="medical-card">
                        <CardContent className="p-4 text-center">
                          <p className="text-xs text-slate-500 mb-1">{r.label} ({d.year})</p>
                          <p className={`text-3xl font-bold text-${r.color}-600`}>{r.value}</p>
                          <p className={`text-xs mt-1 text-${r.color}-500`}>
                            {r.color === "green" ? "Bon" : r.color === "amber" ? "Attention" : "Critique"}
                          </p>
                        </CardContent>
                      </Card>
                    ));
                  }).flat()}
                </div>
              </>
            )}
          </div>
        )}

        {/* Benchmark Tab */}
        {activeTab === "benchmark" && (
          <div className="space-y-6">
            {/* Controls */}
            <Card className="medical-card">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-slate-500">Comparer par</Label>
                    <Select value={compareBy} onValueChange={v => { setCompareBy(v); }}>
                      <SelectTrigger className="bg-slate-900 border-white/10 text-white" data-testid="benchmark-compare-by">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900">
                        <SelectItem value="commune">Ma commune</SelectItem>
                        <SelectItem value="ville">Ma ville</SelectItem>
                        <SelectItem value="region">Ma region</SelectItem>
                        <SelectItem value="abidjan">Abidjan (toutes communes)</SelectItem>
                        <SelectItem value="interieur">Villes de l'interieur</SelectItem>
                        <SelectItem value="national">National</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-slate-500">Indicateur</Label>
                    <Select value={parameter} onValueChange={v => { setParameter(v); }}>
                      <SelectTrigger className="bg-slate-900 border-white/10 text-white" data-testid="benchmark-parameter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900">
                        {Object.entries(PARAM_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={loadBenchmark} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="benchmark-refresh-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4 mr-2" />}
                    Comparer
                  </Button>
                </div>
              </CardContent>
            </Card>

            {benchmark && benchmark.comparison.length > 0 ? (
              <>
                <Card className="medical-card" data-testid="benchmark-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-white">
                      {PARAM_LABELS[parameter]} — Comparaison par {compareBy === "commune" ? "commune" : compareBy === "ville" ? "ville" : compareBy === "region" ? "region" : compareBy} ({benchmark.compare_value || "N/A"})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={benchmark.comparison}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="year" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={11} tickFormatter={v => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                        <Tooltip formatter={(v) => v !== null ? `${v.toLocaleString()}` : "N/A"} contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8 }} />
                        <Legend />
                        <Line type="monotone" dataKey="my_value" name="Ma pharmacie" stroke={COLORS.my} strokeWidth={3} dot={{ r: 5 }} connectNulls />
                        <Line type="monotone" dataKey="peer_avg" name={`Moyenne ${compareBy}`} stroke={COLORS.peer} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="global_avg" name="Moyenne nationale" stroke={COLORS.global} strokeWidth={2} strokeDasharray="8 4" dot={{ r: 4 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Comparison Table */}
                <Card className="medical-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-white">Détail comparatif</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="benchmark-table">
                        <thead>
                          <tr className="border-b border-white/10 text-slate-500">
                            <th className="text-left py-2 px-2">Année</th>
                            <th className="text-right py-2 px-2">Ma pharmacie</th>
                            <th className="text-right py-2 px-2">Moy. {compareBy}</th>
                            <th className="text-right py-2 px-2">Moy. nationale</th>
                            <th className="text-right py-2 px-2">Position</th>
                          </tr>
                        </thead>
                        <tbody>
                          {benchmark.comparison.map((row, i) => {
                            const diff = row.my_value && row.peer_avg ? ((row.my_value - row.peer_avg) / row.peer_avg * 100).toFixed(1) : null;
                            return (
                              <tr key={i} className="border-b border-slate-50">
                                <td className="py-2 px-2 font-semibold text-slate-200">{row.year}</td>
                                <td className="py-2 px-2 text-right font-medium text-cyan-400">{row.my_value !== null ? row.my_value.toLocaleString() : "—"}</td>
                                <td className="py-2 px-2 text-right text-amber-400">{row.peer_avg !== null ? row.peer_avg.toLocaleString() : "—"}</td>
                                <td className="py-2 px-2 text-right text-purple-600">{row.global_avg !== null ? row.global_avg.toLocaleString() : "—"}</td>
                                <td className="py-2 px-2 text-right">
                                  {diff !== null && (
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${parseFloat(diff) > 0 ? "bg-green-500/10 text-green-400" : parseFloat(diff) < 0 ? "bg-red-500/10 text-red-400" : "bg-slate-100 text-slate-500"}`}>
                                      {parseFloat(diff) > 0 ? "+" : ""}{diff}%
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="medical-card">
                <CardContent className="p-8 text-center">
                  <GitCompare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">
                    {historicalData.length === 0 ? "Saisissez vos données historiques pour accéder au benchmark." : "Aucune donnée comparative disponible. D'autres pharmacies doivent aussi saisir leurs données."}
                  </p>
                  {historicalData.length === 0 && (
                    <Button onClick={() => setActiveTab("history")} className="mt-2 bg-cyan-600 hover:bg-cyan-700 text-white">
                      Saisir mes données
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
